import React from 'react';
import { FitText } from './RoomNamePlates.tsx';
import EmployeeDetailsPopover from './EmployeeDetailsPopover.tsx';
import type { Employee } from '../services/api.ts';

// Electric-purple accent — matches the purple "Who's free?" time bar on the board timeline.
export const WHOS_FREE_PURPLE = '#a855f7';

// One available provider brick in the "Who's free?" panel.
export interface FreeBody {
  id: string;
  lastName: string;
  fullName: string;
  color: string;            // nameplate color (same palette as the bullpen bricks)
  tag?: string;             // short right-side badge, e.g. "#3" (pecking) or "til 17:00" (shift end)
  subtitle?: string;        // hover context, e.g. "room ended 15:00" / "not yet placed"
  employee: Employee;       // full record so the brick opens the same card as everywhere else
  card?: any;               // the rich EmployeeDetailsPopover prop bundle (bullpenCardFor)
}

export interface FreeColumn { title: string; items: FreeBody[] }

// The "Who's free?" panel — pops up in place of the bullpen when the time bar is grabbed. Lists the
// anesthesiologists, CRNAs, and students who are on the clock at the parked time and out of a room
// (anyone sent home is excluded). Same brick look as the bullpen; click a brick for the full card.
const WhosFreePanel: React.FC<{
  timeLabel: string;
  columns: FreeColumn[];
  onBackToBullpen: () => void;
  backLabel?: string;
}> = ({ timeLabel, columns, onBackToBullpen, backLabel = 'Back to bullpen' }) => {
  const glow = { textShadow: '0 0 8px rgba(168,85,247,0.85), 0 0 2px rgba(168,85,247,0.95)' };
  const totalFree = columns.reduce((n, c) => n + c.items.length, 0);

  const brick = (b: FreeBody) => {
    const inner = (
      <div
        title={b.subtitle ? `${b.fullName} — ${b.subtitle}` : b.fullName}
        style={{
          background: b.color, color: '#fff', fontWeight: 600, borderRadius: '5px',
          padding: '0 6px', minHeight: '22px', display: 'flex', alignItems: 'center', gap: '5px',
          overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.2)', cursor: 'pointer',
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}><FitText text={b.lastName} /></span>
        {b.tag && <span style={{ fontSize: '0.66em', opacity: 0.9, fontWeight: 700, flexShrink: 0 }}>{b.tag}</span>}
      </div>
    );
    return (
      <EmployeeDetailsPopover
        key={b.id}
        {...(b.card || {})}
        employee={b.employee}
        triggerStyle={{ display: 'block', width: '100%', textDecoration: 'none' }}
      >
        {inner}
      </EmployeeDetailsPopover>
    );
  };

  const column = (col: FreeColumn) => (
    <div key={col.title} style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontSize: '0.7em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--text-muted)', marginBottom: '6px' }}>{col.title} ({col.items.length})</div>
      <div style={{ maxHeight: '66vh', overflowY: 'auto', overflowX: 'hidden', paddingRight: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {col.items.length === 0 ? <div style={{ fontSize: '0.72em', color: 'var(--text-faint)' }}>None</div> : col.items.map(brick)}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '2px' }}>
        <div style={{ fontSize: '1.05em', fontWeight: 800, letterSpacing: '0.4px', color: WHOS_FREE_PURPLE, ...glow }}>Who's free?</div>
        <button
          onClick={onBackToBullpen}
          title="Return to the bullpen"
          style={{ border: `1px solid ${WHOS_FREE_PURPLE}`, background: 'transparent', color: WHOS_FREE_PURPLE, borderRadius: '999px', padding: '2px 10px', cursor: 'pointer', fontSize: '0.72em', fontWeight: 700, whiteSpace: 'nowrap' }}
        >← {backLabel}</button>
      </div>
      {/* The parked time the bar was dragged to, also in glowing purple. */}
      <div style={{ fontSize: '0.98em', fontWeight: 700, color: WHOS_FREE_PURPLE, marginBottom: '8px', ...glow }}>{timeLabel}</div>
      <div style={{ fontSize: '0.72em', color: 'var(--text-secondary)', marginBottom: '10px' }}>
        On the clock at {timeLabel} and out of a room — shift-workers whose shift still covers this time, and pecking-order staff not yet sent home. {totalFree === 0 ? 'No one is free at this time.' : `${totalFree} free.`}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {columns.map(column)}
      </div>
    </div>
  );
};

export default WhosFreePanel;
